import type { Experiment, Placement } from '../../types';
import { isTestPath, type TestPath } from '../../utils/paths';
import type { InWhopDraft, WizardData, WizardVariant } from './wizardTypes';

/** The wizard's key for its one in_whop placement. */
export const IN_WHOP_KEY = 'in_whop';

/** Where a reopened wizard (`/experiments/new?id=…`) picks up. */
export interface ResumedDraft {
  path: TestPath;
  /** 2: place it (no spots yet), 3: go live, 4: live, waiting for the first visit. */
  step: 2 | 3 | 4;
  isUnlock: boolean;
  data: WizardData;
  /** Keyed like the wizard's created placements, so none is made twice. */
  created: Record<string, Placement>;
  inWhop: InWhopDraft;
}

export type ResumePlan =
  | { kind: 'resume'; draft: ResumedDraft }
  /** Ended, archived, or nothing left to set up: the results page. */
  | { kind: 'results' }
  /** An unlock draft whose image never finished uploading: start again. */
  | { kind: 'incomplete_unlock' };

/** The saved test as the wizard's form data (prices in minor units). */
export function experimentToWizardData(experiment: Experiment): WizardData {
  const variants = [...(experiment.variants ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const unlockTitle = experiment.unlock?.title?.trim();
  return {
    companyId: experiment.companyId,
    productId: experiment.offerType === 'unlock' ? '' : experiment.productId,
    productName: unlockTitle || experiment.productName || '',
    basePlanId: '',
    name: experiment.name,
    currency: (variants[0]?.currency ?? 'usd').toLowerCase(),
    variants: variants.map(
      (v): WizardVariant => ({
        name: v.name,
        price: v.price,
        interval: v.interval,
        trialDays: v.trialDays ?? 0,
        ...(v.initialFee ? { initialFee: v.initialFee, hasInitialFee: true } : {}),
        ...(v.packageLabel && { packageLabel: v.packageLabel }),
        ...(v.whopPromoCode && {
          promoCodeId: v.whopPromoCodeId,
          promoCode: v.whopPromoCode,
          promoAmountOff: v.whopPromoAmountOff,
          promoType: v.whopPromoType,
          promoDuration: v.whopPromoDuration,
          promoDurationMonths: v.whopPromoDurationMonths,
        }),
      })
    ),
    split: { ...experiment.split },
  };
}

export function pathFromPlacements(placements: Placement[]): TestPath | null {
  if (placements.some((p) => p.kind === 'in_whop')) return 'in_whop';
  if (placements.some((p) => p.kind === 'partner')) return 'partner';
  if (placements.some((p) => p.kind === 'link')) return 'link';
  return null;
}

/**
 * Decide where to reopen a saved test. The path comes from its spots, else
 * the URL's ?path= (a draft whose spots were never made), else a link.
 */
export function planResume(
  experiment: Experiment,
  placements: Placement[],
  requestedPath: string | null
): ResumePlan {
  if (experiment.archived || (experiment.status !== 'draft' && experiment.status !== 'running')) {
    return { kind: 'results' };
  }
  const isUnlock = experiment.offerType === 'unlock';
  if (isUnlock && !experiment.unlock?.contentKey) {
    return experiment.status === 'draft' ? { kind: 'incomplete_unlock' } : { kind: 'results' };
  }

  let path: TestPath = pathFromPlacements(placements) ?? (isTestPath(requestedPath) ? requestedPath : 'link');
  // Unlocks sell on a hosted page: never inside a whop.
  if (isUnlock && path === 'in_whop') path = 'link';

  if (experiment.status === 'running' && placements.length === 0) return { kind: 'results' };
  const step = experiment.status === 'running' ? 4 : placements.length > 0 ? 3 : 2;

  const created: Record<string, Placement> = {};
  for (const p of placements) created[p.kind === 'in_whop' ? IN_WHOP_KEY : p.id] = p;
  const inWhopPlacement = placements.find((p) => p.kind === 'in_whop');

  return {
    kind: 'resume',
    draft: {
      path,
      step,
      isUnlock,
      data: experimentToWizardData(experiment),
      created,
      inWhop: {
        freeProductId: inWhopPlacement?.productId ?? '',
        freeProductName: inWhopPlacement?.label ?? '',
      },
    },
  };
}
